import * as React from "react";
import SearchForm from "../../../containers/SearchForm";
import { Form, FormSelectChoice, KeyValue, Query } from "../../../interfaces";
import { Typography, List, ListItem, Divider } from "@mui/material";
import SelectFilterField from "../../SelectFilterField";
import { useState } from "react";
import {
  summaryTablesInstitutionTypeOptions,
  summaryTablesReportTypeOptions,
  summaryTablesTotalsTypeOptions,
} from "../../../refdata/constants";
import useAppStateToFormChanges from "../../../hooks/useAppStateToFormChanges";
import { yearChoice } from "../../../common/services/Utilities";
import useLastYearFilter from "../../../hooks/useLastYearFilter";
import { selectedStatesAlpha } from "../../../refdata/FilterOptions";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";

export interface SummaryTablesForm extends Form {
  reportType: string;
  lastYear: string;
  stname: string;
  stalpbr: string;
  county: string;
  institutionTypeTimeSeries: string;
  totalsType: string;
  startDate: string;
  endDate: string;
}

const divider = (
  <Divider
    style={{ margin: 0, backgroundColor: "#acc8df" }}
    variant="middle"
    component="li"
  />
);

export default function SummaryTablesSearchForm({
  appState,
  updateFilter,
}: {
  appState: Query;
  updateFilter: (filterField: string, value: string | number) => void;
}) {
  const { lastYear } = useLastYearFilter(appState.filters, updateFilter, true);

  const filters = {
    lastYear: appState.filters.lastYear,
    stname: appState.filters.stname,
    stalpbr: appState.filters.stalpbr,
    county: appState.filters.county,
    startDate: appState.filters.startDate,
    endDate: appState.filters.endDate,
    institutionTypeTimeSeries: appState.ui.institutionTypeTimeSeries,
    totalsType: appState.ui.totalsType,
  };

  const countyLabel: FormSelectChoice = {
    label: "Select County (optional)",
    value: "",
  };
  const [counties, setCounties] = React.useState<FormSelectChoice[]>([]);

  const initialFormState = {
    ...filters,
    reportType: appState.ui.reportType,
  };
  const [summaryTablesForm, setSummaryTablesForm] =
    useState<SummaryTablesForm>(initialFormState);

  React.useEffect(() => {
    if (appState.ui.reportType && !summaryTablesForm.reportType) {
      setSummaryTablesForm({
        ...summaryTablesForm,
        reportType: appState.ui.reportType,
      });
    }
    if (
      appState.ui.institutionTypeTimeSeries &&
      appState.ui.totalsType &&
      !summaryTablesForm.totalsType &&
      !summaryTablesForm.institutionTypeTimeSeries
    ) {
      setSummaryTablesForm({
        ...summaryTablesForm,
        totalsType: appState.ui.totalsType,
        institutionTypeTimeSeries: appState.ui.institutionTypeTimeSeries,
      });
    }
  }, [
    appState.ui.institutionTypeTimeSeries,
    appState.ui.reportType,
    appState.ui.totalsType,
    summaryTablesForm,
  ]);

  const stateSelectionReports = ["stateCharterClass", "stateCounty"];

  const stateFormSelection = stateSelectionReports.includes(
    summaryTablesForm.reportType,
  )
    ? "stalpbr"
    : "stname";

  const summaryTablesReportingPeriods = yearChoice(1994, lastYear, "06/30/");

  const { prevForm, setPrevForm } = useAppStateToFormChanges<SummaryTablesForm>(
    appState.filters,
    appState.search,
    setSummaryTablesForm,
  );

  const arrayOfCounties = counties.map((county: KeyValue) => county.value);

  React.useEffect(() => {
    if (
      appState.filters.stalpbr !== summaryTablesForm.stalpbr &&
      summaryTablesForm.reportType === "stateCounty" &&
      !arrayOfCounties.includes(summaryTablesForm.county) &&
      summaryTablesForm.county
    ) {
      setSummaryTablesForm({ ...summaryTablesForm, county: "" });
    }
  }, [appState.filters.stalpbr, arrayOfCounties, summaryTablesForm]);

  React.useEffect(() => {
    if (
      summaryTablesForm.reportType === "stateCounty" &&
      summaryTablesForm[stateFormSelection]
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=STALPBR:"${
            summaryTablesForm[stateFormSelection]
          }" AND YEAR:${
            summaryTablesForm.lastYear
          }&agg_by=CNTYNAMB&agg_limit=10000`,
        )
          .then((response) => {
            if (isMounted) {
              const allCounties = response.data.map(
                (e: KeyValue) => e.CNTYNAMB,
              );
              const transformedData: any = allCounties.map(
                (item: KeyValue) => ({
                  label: item,
                  value: item,
                }),
              );
              setCounties(transformedData);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [stateFormSelection, summaryTablesForm]);

  const label: FormSelectChoice = {
    label: "Select State/Territory",
    value: "",
  };

  const shouldDisable =
    summaryTablesForm.startDate > summaryTablesForm.endDate ||
    (summaryTablesForm.reportType === "nationalTimeSeries" &&
      !summaryTablesForm.institutionTypeTimeSeries) ||
    (summaryTablesForm.reportType === "nationalTimeSeries" &&
      !summaryTablesForm.totalsType) ||
    (stateSelectionReports.includes(summaryTablesForm.reportType) &&
      !summaryTablesForm[stateFormSelection]);

  const shouldDisableOnLevelTwo =
    appState.ui.summaryTableLevelTwo &&
    summaryTablesForm.reportType === "nationalTimeSeries" &&
    appState.ui.reportType === "nationalTimeSeries";

  const SummaryTablesFormList = (
    <List>
      <ListItem>
        <Typography
          sx={{ paddingTop: "16px", lineHeight: "1rem" }}
          variant="h4"
        >
          Report Type
        </Typography>
        <SelectFilterField
          currentValue={summaryTablesForm.reportType}
          fieldName="reportType"
          options={summaryTablesReportTypeOptions}
          form={summaryTablesForm}
          setForm={setSummaryTablesForm}
        />
      </ListItem>
      {divider}
      {(summaryTablesForm.reportType === "stateCharterClass" ||
        summaryTablesForm.reportType === "stateCounty") && (
        <>
          <ListItem>
            <Typography
              sx={{ paddingTop: "16px", lineHeight: "1rem" }}
              variant="h4"
            >
              Locations
            </Typography>
            <SelectFilterField
              currentValue={summaryTablesForm[stateFormSelection]}
              fieldName={stateFormSelection}
              options={[label, ...selectedStatesAlpha]}
              form={summaryTablesForm}
              setForm={setSummaryTablesForm}
            />
            {summaryTablesForm.reportType === "stateCounty" && (
              <SelectFilterField
                currentValue={summaryTablesForm.county}
                fieldName="county"
                options={[countyLabel, ...counties]}
                form={summaryTablesForm}
                disabled={shouldDisable}
                setForm={setSummaryTablesForm}
              />
            )}
          </ListItem>
          {divider}
        </>
      )}
      {summaryTablesForm.reportType !== "nationalTimeSeries" ? (
        <ListItem>
          <Typography
            sx={{ paddingTop: "16px", lineHeight: "1rem" }}
            variant="h4"
          >
            Reporting Period Ending
          </Typography>
          <SelectFilterField
            currentValue={summaryTablesForm.lastYear}
            fieldName="lastYear"
            options={summaryTablesReportingPeriods}
            form={summaryTablesForm}
            setForm={setSummaryTablesForm}
          />
        </ListItem>
      ) : (
        <>
          <ListItem>
            <Typography
              sx={{ paddingTop: "16px", lineHeight: "1rem" }}
              variant="h4"
            >
              Additional Details
            </Typography>
            <SelectFilterField
              id="institutionTypeTimeSeries"
              currentValue={summaryTablesForm.institutionTypeTimeSeries}
              fieldName="institutionTypeTimeSeries"
              options={summaryTablesInstitutionTypeOptions}
              form={summaryTablesForm}
              setForm={setSummaryTablesForm}
              error={!summaryTablesForm.institutionTypeTimeSeries}
              helperText={
                !summaryTablesForm.institutionTypeTimeSeries
                  ? "Selection Required"
                  : ""
              }
              disabled={shouldDisableOnLevelTwo}
            />
            <SelectFilterField
              currentValue={summaryTablesForm.totalsType}
              fieldName="totalsType"
              options={summaryTablesTotalsTypeOptions}
              form={summaryTablesForm}
              setForm={setSummaryTablesForm}
              error={!summaryTablesForm.totalsType}
              helperText={
                !summaryTablesForm.totalsType ? "Selection Required" : ""
              }
              disabled={shouldDisableOnLevelTwo}
            />
          </ListItem>
          {divider}
          <ListItem>
            <Typography
              sx={{ paddingTop: "16px", lineHeight: "1rem" }}
              variant="h4"
            >
              Reporting Period Range
            </Typography>
            <SelectFilterField
              helperText={
                summaryTablesForm.startDate > summaryTablesForm.endDate
                  ? "Start Date can not be after End Date"
                  : undefined
              }
              label={"Start Date"}
              currentValue={summaryTablesForm.startDate}
              fieldName="startDate"
              options={summaryTablesReportingPeriods}
              form={summaryTablesForm}
              noEmptyVal
              setForm={setSummaryTablesForm}
              error={summaryTablesForm.startDate > summaryTablesForm.endDate}
              disabled={shouldDisableOnLevelTwo}
            />
            <SelectFilterField
              helperText={
                summaryTablesForm.startDate > summaryTablesForm.endDate
                  ? "End Date can not be before Start Date"
                  : undefined
              }
              label={"End Date"}
              currentValue={summaryTablesForm.endDate}
              fieldName="endDate"
              options={summaryTablesReportingPeriods}
              form={summaryTablesForm}
              noEmptyVal
              setForm={setSummaryTablesForm}
              error={summaryTablesForm.startDate > summaryTablesForm.endDate}
              disabled={shouldDisableOnLevelTwo}
            />
          </ListItem>
          {divider}
        </>
      )}
    </List>
  );
  return (
    <SearchForm
      scroll
      prevForm={prevForm}
      setPrevForm={setPrevForm}
      canHideSearchPanel={false}
      formList={SummaryTablesFormList}
      form={summaryTablesForm}
      searchButtonText={"Generate Report"}
      disabled={shouldDisable || shouldDisableOnLevelTwo}
    />
  );
}
